import type { UseAppForm } from '../../../form';
import type { FieldRulePermissions } from '../../FieldPermissions/FieldRuleCheckbox/FieldRuleCheckbox';
import type { PermissionContentTypeDefinition } from '../../PermissionContentType/PermissionContentType';

import { useStore } from '@tanstack/react-form';
import { useCallback, useEffect, useMemo } from 'react';

import { deepReadKeyValue } from '../../../../lib/helpers';

import { useFieldContext, useFormContext } from '../../../form';

interface GlobalRuleCheckboxProps {
  /**
   * Permission rule name
   */
  ruleName: string;
  /**
   * If field is disabled
   */
  disabled?: boolean;
  /**
   * Test id for field
   */
  testId?: string;
}

type GlobalRulePermissions = {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
};

const GlobalRuleCheckbox = ({
  ruleName,
  disabled = false,
  testId = '',
}: GlobalRuleCheckboxProps) => {
  const form = useFormContext() as unknown as UseAppForm;
  const field = useFieldContext<GlobalRulePermissions>();

  const permission = useStore(
    form.store,
    (state) =>
      deepReadKeyValue(field.name, state.values) as {
        type: string;
        attributesPermission: FieldRulePermissions;
        contentTypeDefinition: PermissionContentTypeDefinition;
      } & GlobalRulePermissions,
  );

  const permissionType = permission?.type;
  const isCoType = permissionType === 'CO';

  const attributesPermission = useMemo(
    () => permission?.attributesPermission,
    [permission?.attributesPermission],
  );

  const fieldsChecked = useMemo(
    () =>
      attributesPermission && ruleName in attributesPermission
        ? attributesPermission[ruleName as keyof FieldRulePermissions]
        : undefined,
    [attributesPermission, ruleName],
  );

  const order = useMemo(
    () => permission?.contentTypeDefinition?.metaDefinition?.order || [],
    [permission?.contentTypeDefinition?.metaDefinition?.order],
  );

  const indeterminate = useMemo(
    () =>
      order.length > 0 &&
      fieldsChecked &&
      fieldsChecked.length > 0 &&
      fieldsChecked.length < order.length,
    [fieldsChecked, order.length],
  );

  useEffect(() => {
    if (!permission) return;

    const fieldName = `${field.name}.${ruleName}`;

    if (
      !form.getFieldValue(fieldName) &&
      (indeterminate ||
        (order.length > 0 && fieldsChecked?.length === order.length))
    ) {
      form.setFieldValue(fieldName, true, { dontRunListeners: true });
    } else if (
      form.getFieldValue(fieldName) &&
      order.length > 0 &&
      fieldsChecked?.length === 0
    ) {
      form.setFieldValue(fieldName, false, { dontRunListeners: true });
    }
  }, [
    field.name,
    fieldsChecked?.length,
    form,
    indeterminate,
    order.length,
    permission,
    ruleName,
  ]);

  const updateCanReadRules = useCallback(
    (value: boolean) => {
      form.setFieldValue(`${field.name}.canRead`, true);
      if (isCoType && order?.length > 0) {
        form.setFieldValue(
          `${field.name}.attributesPermission.canRead`,
          value ? order : [],
        );
      }
    },
    [field.name, form, isCoType, order],
  );

  const updateAttributePermissions = useCallback(
    (value: boolean) => {
      if (['canCreate', 'canUpdate'].indexOf(ruleName) > -1) {
        form.setFieldValue(
          `${field.name}.attributesPermission.${ruleName}`,
          value ? order : [],
        );

        if (value) {
          form.setFieldValue(
            `${field.name}.attributesPermission.canRead`,
            value ? order : [],
          );
        }
      }

      if (ruleName === 'canRead') {
        form.setFieldValue(
          `${field.name}.attributesPermission.${ruleName}`,
          value
            ? order
            : order.filter(
                (propertyKey) =>
                  attributesPermission?.canCreate?.includes(propertyKey) ||
                  attributesPermission?.canUpdate?.includes(propertyKey),
              ),
        );
      }
    },
    [
      attributesPermission?.canCreate,
      attributesPermission?.canUpdate,
      field.name,
      form,
      order,
      ruleName,
    ],
  );

  const fieldListeners = useMemo(
    () =>
      !permissionType
        ? {}
        : {
            onChange: ({ value }: { value: boolean }) => {
              if (value && ['canCreate', 'canUpdate'].indexOf(ruleName) > -1) {
                updateCanReadRules(value);
              }

              if (isCoType && order.length > 0) {
                updateAttributePermissions(value);
              }
            },
          },
    [
      isCoType,
      order.length,
      permissionType,
      ruleName,
      updateAttributePermissions,
      updateCanReadRules,
    ],
  );

  const checkboxDisabled = useMemo(() => {
    if (disabled) return true;
    if (!permissionType) return false;
    if (ruleName === 'canRead') {
      return (
        ((!isCoType || order.length === 0) &&
          (permission?.canCreate || permission?.canUpdate)) ||
        (order.length > 0 &&
          new Set([
            ...(attributesPermission?.canCreate || []),
            ...(attributesPermission?.canUpdate || []),
          ]).size === order.length)
      );
    }
  }, [
    attributesPermission?.canCreate,
    attributesPermission?.canUpdate,
    disabled,
    isCoType,
    order.length,
    permission?.canCreate,
    permission?.canUpdate,
    permissionType,
    ruleName,
  ]);

  return (
    <form.AppField
      name={`${field.name}.${ruleName}`}
      listeners={fieldListeners}
    >
      {(subField) => (
        <subField.Checkbox
          indeterminate={indeterminate}
          disabled={checkboxDisabled}
          testId={testId}
        />
      )}
    </form.AppField>
  );
};

export default GlobalRuleCheckbox;
